import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { authAPI } from "@/services/api";
import { applyAuthSession } from "@/utils/authSession";
import { getApiErrorMessage } from "@/utils/apiError";
import FirebaseAuthPanel from "@/components/auth/FirebaseAuthPanel";
import AuthShell from "@/components/auth/AuthShell";
import { isFirebaseConfigured, isFirebaseProjectCompatible, signInWithEmail } from "@/lib/firebase";
import { Eye, EyeOff, Loader2 } from "lucide-react";

const ClientLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [firebaseEnabled, setFirebaseEnabled] = useState(false);

  useEffect(() => {
    let mounted = true;
    authAPI.getConfig()
      .then((config) => {
        if (mounted) {
          setFirebaseEnabled(Boolean(
            config.enabled && isFirebaseConfigured() && isFirebaseProjectCompatible(config.project_id),
          ));
        }
      })
      .catch(() => {
        if (mounted) setFirebaseEnabled(false);
      });
    return () => { mounted = false; };
  }, []);

  const handleFirebaseSession = (session) => {
    applyAuthSession(session, onLogin);
  };

  const handleFirebaseEmailLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }
    if (!firebaseEnabled) {
      return handleLogin(e);
    }
    setLoading(true);
    try {
      const idToken = await signInWithEmail(email, password);
      const session = await authAPI.firebaseSession({
        id_token: idToken,
        role: "client",
      });
      toast.success("Login successful!");
      handleFirebaseSession(session);
    } catch (error) {
      const code = error?.code;
      if (code === "auth/invalid-credential" || code === "auth/wrong-password") {
        toast.error("Invalid email or password");
      } else {
        toast.error(getApiErrorMessage(error, "Login failed"));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const response = await authAPI.loginClient(email, password);
      toast.success("Login successful!");
      applyAuthSession(response.data, onLogin);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Invalid credentials"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      eyebrow="Client workspace"
      title="Sign In"
      description="Enter your email and password to sign in to your business account."
      panelTitle="Client growth workspace"
      panelDescription="Launch Prove-Requests, manage BRMs, inspect verified enrollments, and connect conversion APIs from one Proovam workspace."
      backLabel="Back to dashboard"
    >
      <div className="space-y-7">
        <FirebaseAuthPanel role="client" onSession={handleFirebaseSession} disabled={loading} />

        <form onSubmit={firebaseEnabled ? handleFirebaseEmailLogin : handleLogin} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium text-gray-700">
              Email <span className="text-[#f04438]">*</span>
            </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11 rounded-lg border-gray-200 bg-white px-4 text-sm shadow-theme-xs placeholder:text-gray-400 focus:border-brand-400"
                  data-testid="client-email-input"
                />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm font-medium text-gray-700">
              Password <span className="text-[#f04438]">*</span>
            </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11 rounded-lg border-gray-200 bg-white px-4 pr-11 text-sm shadow-theme-xs placeholder:text-gray-400 focus:border-brand-400"
                    data-testid="client-password-input"
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    onClick={() => setShowPassword((visible) => !visible)}
                    className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-3 text-sm text-gray-700">
              <span className="h-5 w-5 rounded-md border border-gray-300 bg-white" />
              Keep me logged in
            </label>
            <button type="button" className="text-sm font-medium text-brand-500 hover:text-brand-600">
              Forgot password?
            </button>
          </div>

          <Button type="submit" className="h-11 w-full rounded-lg font-semibold" disabled={loading} data-testid="client-login-btn">
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Sign in
          </Button>
        </form>

        <p className="text-sm text-gray-700">
          Don't have an account?{" "}
          <Link to="/client/register" className="font-medium text-brand-500 hover:text-brand-600" data-testid="register-link">
            Sign Up
          </Link>
        </p>
      </div>
    </AuthShell>
  );
};

export default ClientLogin;
